"use client";

import useSWR from "swr";

import SWR_KEYS from "@/constants/swr-keys";
import IProduct from "@/interfaces/product.interface";
import productService from "@/services/product.service";
import { IFilter } from "@/types/filter.types";
import { PaginatedResponse, RequestPagination, RequestsBaseReturn } from "@/types/requests.types";

import { ProductSortField, SortDirection } from "../../../components/screens/Products/types";

const SWR_CONFIG = {
  revalidateOnFocus: false,
  shouldRetryOnError: false,
  errorRetryCount: 0,
} as const;

export interface UseGetProductsProps extends RequestPagination {
  filters: IFilter<IProduct>[];
  sortField?: ProductSortField | null;
  sortDirection?: SortDirection | null;
  enabled?: boolean;
}

export type UseGetProductsReturn = RequestsBaseReturn<PaginatedResponse<IProduct> | null>;

const normalizeFilters = (filters: IFilter<IProduct>[]): IFilter<IProduct>[] => {
  return filters.map((filter) => {
    const property = filter.property as string;

    if (property === "SKU.Code") {
      return {
        ...filter,
        property: "sku.code" as keyof IProduct,
      };
    }

    if (property === "SKU.Ean") {
      return {
        ...filter,
        property: "sku.ean" as keyof IProduct,
      };
    }

    if (property === "SKU.Kit") {
      return {
        ...filter,
        property: "sku.kit" as keyof IProduct,
      };
    }

    if (property === "SKU.Active") {
      return {
        ...filter,
        property: "sku.active" as keyof IProduct,
      };
    }

    // Normalizar filtro de categoria para o formato esperado pelo backend
    if (property === "linkedcategories.categoryid") {
      return {
        ...filter,
        property: "linkedcategories.categoryid" as keyof IProduct,
      };
    }

    return filter;
  });
};

const useGetProducts = ({
  pageNumber,
  pageSize,
  filters,
  sortField = null,
  sortDirection = null,
  enabled = true,
}: UseGetProductsProps): UseGetProductsReturn => {
  const normalizedFilters = normalizeFilters(filters);
  const sortBy = sortField ?? null;
  const sortDir = sortDirection ?? null;
  const shouldFetch = enabled;

  const {
    data,
    isValidating: isLoading,
    error,
  } = useSWR<PaginatedResponse<IProduct>, Error>(
    shouldFetch
      ? [`${SWR_KEYS.getProducts}`, pageNumber, pageSize, normalizedFilters, sortBy, sortDir]
      : null,
    () =>
      productService.filter(normalizedFilters, { pageNumber, pageSize }, sortBy, sortDir),
    SWR_CONFIG
  );

  return { data: data || null, isLoading, error };
};

export default useGetProducts;
